'use client';

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { getSectorSentiment } from '../../lib/api';
import { getSectorConfig } from '../../lib/api/sector-config';
import { formatDataTimestamp, formatCacheTimestamp, formatArticleDate } from '../../lib/timezone';

interface NewsArticle {
  title: string;
  description: string;
  source: string;
  publishedAt: string;
  url: string;
}

interface SectorSentiment {
  sectors: SectorAnalysis[];
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';
}

interface SectorAnalysis {
  symbol: string;
  name: string;
  quadrant: 'LEADING' | 'WEAKENING' | 'LAGGING' | 'IMPROVING';
  relativeStrength: number;
  sentiment: number; // -1 to +1
  sentimentLabel: 'BEARISH' | 'NEUTRAL' | 'BULLISH';
  trend: 'UP' | 'FLAT' | 'DOWN';
  aiContext: string;
  newsCount: number;
  articles: NewsArticle[];
}

export function SectorSentimentWidget() {
  const [sentiment, setSentiment] = useState<SectorSentiment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch sector configuration from API (v3.5.0)
        const config = await getSectorConfig();
        const selectedSectors = config.enabledSectors;

        console.log('[SectorSentiment] Selected sectors from API:', selectedSectors);

        const data = await getSectorSentiment(selectedSectors);
        console.log('[SectorSentiment] Received sectors:', data.sectors.length);
        setSentiment(data as unknown as SectorSentiment);
      } catch (err) {
        console.error('[SectorSentiment] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch sector sentiment');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    const interval = setInterval(fetchData, 30 * 60 * 1000); // 30 min

    // Listen for custom event (from Settings page)
    const handleConfigChange = () => {
      console.log('[SectorSentiment] Configuration changed (custom event), refetching...');
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('sectorConfigChanged', handleConfigChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('sectorConfigChanged', handleConfigChange);
    };
  }, [refreshTrigger]);

  const getQuadrantColor = (quadrant: string) => {
    switch (quadrant) {
      case 'LEADING':
        return 'success';
      case 'IMPROVING':
        return 'info';
      case 'WEAKENING':
        return 'warning';
      case 'LAGGING':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSentimentColor = (label: string) => {
    switch (label) {
      case 'BULLISH':
        return 'success';
      case 'BEARISH':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'UP':
        return <TrendingUpIcon color="success" fontSize="small" />;
      case 'DOWN':
        return <TrendingDownIcon color="error" fontSize="small" />;
      default:
        return <TrendingFlatIcon color="warning" fontSize="small" />;
    }
  };

  const getSentimentProgress = (value: number) => {
    return ((value + 1) / 2) * 100; // Convert -1 to +1 → 0 to 100
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error || !sentiment) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" icon={<ErrorIcon />}>
            {error || 'Failed to load sector sentiment'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="div" gutterBottom>
          Sector Sentiment Analysis
        </Typography>

        {/* Sector Grid */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {sentiment.sectors.map((sector) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sector.symbol}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 2,
                  borderLeft: '3px solid',
                  borderColor: getQuadrantColor(sector.quadrant) + '.main',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Sector Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {sector.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {sector.symbol}
                    </Typography>
                  </Box>
                  <Chip
                    label={sector.quadrant}
                    color={getQuadrantColor(sector.quadrant) as any}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                {/* Sentiment Score */}
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Sentiment
                      </Typography>
                      {getTrendIcon(sector.trend)}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={sector.sentimentLabel}
                        color={getSentimentColor(sector.sentimentLabel) as any}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {sector.sentiment.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getSentimentProgress(sector.sentiment)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getSentimentColor(sector.sentimentLabel) + '.main',
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>

                {/* AI Context */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontStyle: 'italic',
                    lineHeight: 1.4,
                    minHeight: 48,
                  }}
                >
                  {sector.aiContext}
                </Typography>

                {/* News Articles (Collapsible) */}
                {sector.articles && sector.articles.length > 0 && (
                  <Accordion
                    sx={{
                      mt: 1.5,
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="small" />}
                      sx={{
                        minHeight: 36,
                        '& .MuiAccordionSummary-content': {
                          margin: '6px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        },
                      }}
                    >
                      <ArticleIcon fontSize="small" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight={600}>
                        {sector.newsCount} Articles
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1 }}>
                      <Stack spacing={1}>
                        {sector.articles.map((article, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1,
                              backgroundColor: 'rgba(255, 255, 255, 0.03)',
                              borderRadius: 1,
                              borderLeft: '2px solid',
                              borderColor: 'primary.main',
                            }}
                          >
                            <Link
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              color="inherit"
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                {article.title}
                              </Typography>
                            </Link>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {article.source}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {formatArticleDate(article.publishedAt).replace(/, \d{4}/, '')}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          Powered by Dual AI (GPT-OSS + DistilBERT) • On-demand analysis • Sector data 10m
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Data sourced: {formatDataTimestamp(sentiment.timestamp)}
          </Typography>
          {sentiment.cachedAt && (
            <Typography
              variant="caption"
              sx={{
                color: sentiment.cacheStatus === 'HIT' ? '#4caf50' : sentiment.cacheStatus === 'STALE_REFRESH' ? '#ff9800' : '#9e9e9e',
                fontWeight: 600
              }}
            >
              Cache: {sentiment.cacheStatus}
              {' '}({formatCacheTimestamp(sentiment.cachedAt)})
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
